import { usePrivateMutation as useMutation } from "@/lib/query/core/use-private-mutation";
import { useQueryClient } from "@tanstack/react-query";
import type { StoreSubscriptionPaymentView } from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import { invalidateAdminStoreSubscriptions } from "@/lib/query/core/invalidation";

type ReverseStoreSubscriptionPaymentInput = {
  storeId: string;
  paymentId: string;
  reason: string;
};

/**
 * Reverses a recorded payment — the accounting undo that replaced delete.
 * The payment stays in the list, marked reversed; the wallet drops by its
 * amount. Refreshes every subscription read for the store, because the
 * balance, the ledger and the list all change together.
 */
export function useReverseStoreSubscriptionPaymentMutation() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({ storeId, paymentId, reason }: ReverseStoreSubscriptionPaymentInput) =>
      apiJsonClient<StoreSubscriptionPaymentView>(
        endpoints.admin.storeSubscriptionPaymentReverse(storeId, paymentId),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ reason }),
        },
      ),
    onSuccess: async (_data, variables) => {
      await invalidateAdminStoreSubscriptions(queryClient, variables.storeId);
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  };
}
